// src/components/CallTypeModal.jsx
// Simple pre-call modal with optional recording
import React, { useState } from "react";
import { Phone, Mic, MicOff, X } from "lucide-react";

const CallTypeModal = ({ client, isOpen, onClose, onCall, onCallSuccess }) => {
  const [recordCall, setRecordCall] = useState(false);
  const [calling, setCalling] = useState(false);
  const [callInitiated, setCallInitiated] = useState(false);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setCalling(false);
      setCallInitiated(false);
      setRecordCall(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCall = async () => {
    // Prevent double-clicks
    if (calling || callInitiated) return;

    console.log('[CallTypeModal] Starting call...');
    setCalling(true);
    setCallInitiated(true);

    // Always close this modal and open outcome modal
    // The API call is for Twilio click-to-call, but the outcome modal
    // should appear regardless (user may be calling manually)

    // Attempt the API call but don't block the UI flow on it
    try {
      await onCall({ record: recordCall });
    } catch (err) {
      console.log('[CallTypeModal] API call failed (may not have Twilio configured):', err.message);
      // Continue anyway - user can still log the outcome
    }

    // Close this modal
    onClose();

    // Open the outcome modal after a brief delay to ensure state updates
    setTimeout(() => {
      console.log('[CallTypeModal] Opening outcome modal');
      if (onCallSuccess) {
        onCallSuccess();
      }
    }, 150);

    setCalling(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#172A3A] to-slate-800 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFB800] to-amber-500 flex items-center justify-center">
                <Phone size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Call Client</h2>
                <p className="text-slate-300 text-sm">
                  {client?.firstName} {client?.lastName}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Call Options */}
        <div className="p-6 space-y-4">
          {/* Phone number display */}
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <div className="text-sm text-slate-500 mb-1">Calling</div>
            <div className="text-xl font-bold text-slate-900">{client?.phone}</div>
          </div>

          {/* Record toggle */}
          <div
            onClick={() => setRecordCall(!recordCall)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              recordCall
                ? 'border-red-500 bg-red-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  recordCall ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {recordCall ? <Mic size={20} /> : <MicOff size={20} />}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">
                    {recordCall ? 'Recording Enabled' : 'Recording Disabled'}
                  </div>
                  <div className="text-sm text-slate-500">
                    {recordCall
                      ? 'Call will be recorded and saved to client profile'
                      : 'Click to enable call recording'
                    }
                  </div>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                recordCall ? 'bg-red-500' : 'bg-slate-300'
              }`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  recordCall ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl border border-slate-300 font-medium hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCall}
            disabled={calling}
            className="flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 bg-gradient-to-r from-[#FFB800] to-amber-500 text-white hover:from-amber-500 hover:to-[#FFB800] shadow-lg hover:shadow-xl disabled:opacity-50"
          >
            {calling ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Calling...
              </>
            ) : (
              <>
                <Phone size={18} />
                {recordCall ? 'Call & Record' : 'Start Call'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallTypeModal;
